import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Fetch consolidated user dashboard data (single API call)
 * This replaces multiple individual API calls for better performance
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Complete user dashboard data
 * 
 * Expected Response:
 * {
 *   success: true,
 *   data: {
 *     user: { id, firstName, lastName, email, role, createdAt },
 *     stats: { totalCoursesEnrolled, totalTopics, totalTopicsCompleted, overallProgress, currentStreak, longestStreak, totalXp },
 *     weeklyActivity: { last7Days: [...], summary: { totalTopicsThisWeek, totalMinutesThisWeek, averagePerDay } },
 *     courseProgress: [{ courseId, courseName, progress, completedTopics, totalTopics, lastAccessedAt }],
 *     topicsByType: { theory: { total, completed }, mcq: { total, completed }, coding: { total, completed } },
 *     achievements: [{ id, icon, label, unlocked, unlockedAt }],
 *     recentSubmissions: [{ topicId, topicTitle, type, score, submittedAt }]
 *   },
 *   generatedAt: string
 * }
 */
export const fetchUserDashboard = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/dashboard`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching user dashboard:", error);
    throw error;
  }
};

/**
 * Fetch consolidated admin analytics dashboard (single API call)
 * This replaces fetching all jobs and users separately
 * @param {string} token - JWT admin auth token
 * @param {Object} options - Optional query parameters
 * @param {string} options.dateRange - Number of days (7, 30, 90, 365)
 * @param {number} options.months - Number of months for trend data
 * @returns {Promise<Object>} Complete admin analytics data
 * 
 * Expected Response:
 * {
 *   success: true,
 *   data: {
 *     summary: { totalJobs, activeJobs, expiredJobs, totalUsers, totalApplies, totalLikes },
 *     jobTypeDistribution: [{ name, value, color }],
 *     jobStatusDistribution: [{ name, value, color }],
 *     jobsByMonth: [{ name, jobs }],
 *     applicationsByMonth: [{ name, applications, likes }],
 *     usersByMonth: [{ name, users }],
 *     topCompanies: [{ name, jobs }],
 *     experienceDistribution: [{ name, value, color }],
 *     recentActivity: { last24Hours: {...}, last7Days: {...} }
 *   },
 *   generatedAt: string
 * }
 */
export const fetchAdminAnalytics = async (token, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.dateRange) params.append("dateRange", options.dateRange);
    if (options.months) params.append("months", options.months);
    
    const queryString = params.toString();
    const url = `${BASE_URL}/admin/analytics/dashboard${queryString ? `?${queryString}` : ""}`;
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching admin analytics:", error);
    throw error;
  }
};

/**
 * Fetch user's dashboard statistics
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} User dashboard stats
 * 
 * Expected Response:
 * {
 *   totalCoursesEnrolled: number,
 *   totalTopicsCompleted: number,
 *   totalTopics: number,
 *   overallProgress: number, // 0-100
 *   currentStreak: number, // days
 *   longestStreak: number,
 *   totalXp: number,
 *   lastActiveAt: string, // ISO date
 *   courseProgress: [
 *     {
 *       courseId: string,
 *       courseName: string,
 *       totalTopics: number,
 *       completedTopics: number,
 *       progress: number, // 0-100
 *       lastAccessedAt: string
 *     }
 *   ]
 * }
 */
export const fetchDashboardStats = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/dashboard/stats`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    throw error;
  }
};

/**
 * Fetch user's weekly activity (for charts)
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Weekly activity data
 * 
 * Expected Response:
 * {
 *   weeklyActivity: [
 *     { day: "Mon", date: "2025-12-30", topicsCompleted: 5, minutesSpent: 45 },
 *     { day: "Tue", date: "2025-12-31", topicsCompleted: 3, minutesSpent: 30 },
 *     ...
 *   ],
 *   totalThisWeek: {
 *     topicsCompleted: number,
 *     minutesSpent: number
 *   }
 * }
 */
export const fetchWeeklyActivity = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/dashboard/weekly-activity`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching weekly activity:", error);
    throw error;
  }
};

/**
 * Fetch user's achievements/badges
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} User achievements
 * 
 * Expected Response:
 * {
 *   achievements: [
 *     {
 *       id: string,
 *       name: string,
 *       description: string,
 *       icon: string, // icon class like "ri-fire-line"
 *       color: string, // gradient like "from-orange-500 to-red-500"
 *       unlocked: boolean,
 *       unlockedAt: string | null, // ISO date if unlocked
 *       progress: number, // 0-100 for partially completed
 *       requirement: string // e.g., "Complete 7 days in a row"
 *     }
 *   ],
 *   totalUnlocked: number,
 *   totalAchievements: number
 * }
 */
export const fetchAchievements = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/dashboard/achievements`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching achievements:", error);
    throw error;
  }
};

/**
 * Record user activity (call when user views/completes a topic)
 * This updates streak, XP, and activity tracking
 * @param {Object} params
 * @param {string} params.courseId - Course ID
 * @param {string} params.topicId - Topic ID
 * @param {string} params.activityType - "view" | "complete" | "submit"
 * @param {number} params.timeSpent - Time spent in seconds
 * @param {string} token - JWT auth token
 * 
 * Expected Response:
 * {
 *   success: boolean,
 *   currentStreak: number,
 *   xpEarned: number,
 *   totalXp: number,
 *   newAchievements: [] // Array of newly unlocked achievements
 * }
 */
export const recordActivity = async ({ courseId, topicId, activityType, timeSpent }, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/dashboard/activity`,
      { courseId, topicId, activityType, timeSpent },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error recording activity:", error);
    throw error;
  }
};

/**
 * Fetch user's submission statistics (for topic type distribution)
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Submission stats by type
 * 
 * Expected Response:
 * {
 *   byType: {
 *     theory: { total: number, completed: number },
 *     mcq: { total: number, completed: number, correctAnswers: number },
 *     coding: { total: number, completed: number, passedAll: number }
 *   },
 *   recentSubmissions: [
 *     {
 *       topicId: string,
 *       topicName: string,
 *       type: string,
 *       score: number,
 *       maxScore: number,
 *       submittedAt: string
 *     }
 *   ]
 * }
 */
export const fetchSubmissionStats = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/dashboard/submission-stats`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching submission stats:", error);
    throw error;
  }
};

/**
 * Fetch user's course progress details
 * @param {string} courseId - Course ID
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Detailed course progress
 * 
 * Expected Response:
 * {
 *   courseId: string,
 *   courseName: string,
 *   totalTopics: number,
 *   completedTopics: number,
 *   progress: number,
 *   chapters: [
 *     {
 *       chapterId: string,
 *       chapterName: string,
 *       totalTopics: number,
 *       completedTopics: number,
 *       progress: number,
 *       topics: [
 *         {
 *           topicId: string,
 *           topicName: string,
 *           type: "theory" | "mcq" | "coding",
 *           completed: boolean,
 *           score: number | null,
 *           maxScore: number,
 *           lastAttemptAt: string | null
 *         }
 *       ]
 *     }
 *   ]
 * }
 */
export const fetchCourseProgress = async (courseId, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/dashboard/course-progress/${courseId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching course progress:", error);
    throw error;
  }
};
